// ── QuickRevenueEntry — 빠른 매출 입력 모달 ────────────────────
// 2가지 입력 모드:
//   A) 총액 입력: 카드/현금/배달 분할 (기본)
//   B) 메뉴별 입력: 카테고리 menu items 수량 × 단가 → 자동 합계
// 거래장부에 income transaction(들)으로 저장
// 메뉴별 모드는 lineItems[]에 메뉴 판매 데이터 첨부
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { normalizeMenuName, isMenuMatch } from '../utils/menuProfitability';
import { L, M, MU } from '../i18n/L';   // V160 중국어

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const REVENUE_TYPES = [
  { id: 'sales_card',     get label() { return L("카드"); },  icon: '💳', color: '#1A56A0', get placeholder() { return L("카드 매출 합계"); } },
  { id: 'sales_cash',     get label() { return L("현금"); },  icon: '💵', color: '#18181B', get placeholder() { return L("현금 매출 합계"); } },
  { id: 'sales_delivery', get label() { return L("배달"); },  icon: '🛵', color: '#EA580C', get placeholder() { return L("배달앱 매출 합계"); } },
];

export default function QuickRevenueEntry({
  isOpen,
  defaultDate,        // 'YYYY-MM-DD' or null
  categories = [],    // [{ name, items: [{ id, name, type, selling_price, ... }] }]
  transactions = [],  // 과거 거래장부 — legacy 메뉴 추출용
  onClose,
  onSave,             // async (transactions[]) => void
  isMobile,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState('total');  // 'total' | 'menus'
  const [date, setDate] = useState(defaultDate || today);
  const [amounts, setAmounts] = useState({ sales_card: 0, sales_cash: 0, sales_delivery: 0 });
  const [menuQtys, setMenuQtys] = useState({});  // { menuId: qty }
  const [paymentSplit, setPaymentSplit] = useState({ sales_card: 70, sales_cash: 20, sales_delivery: 10 });
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 사용 가능한 메뉴 — 현재 레시피 메뉴 + 과거 legacy 메뉴
  const availableMenus = useMemo(() => {
    const out = [];
    const seenNorm = new Set();

    // 1) 현재 레시피 메뉴
    (categories || []).forEach(cat => {
      (cat.items || []).forEach(it => {
        if (it.type !== 'menu') return;
        const price = Number(it.selling_price) || 0;
        if (price <= 0) return;
        const norm = normalizeMenuName(it.name);
        if (seenNorm.has(norm)) return;
        seenNorm.add(norm);
        out.push({
          id: it.id || it.name,
          name: it.name,
          price,
          category: cat.name || '',
          isLegacy: false,
        });
      });
    });

    // 2) 거래장부 lineItems에서 발견된 legacy 메뉴
    const legacyAgg = {};
    (transactions || []).forEach(t => {
      if (t?.type !== 'income') return;
      if (!Array.isArray(t.lineItems)) return;
      for (const li of t.lineItems) {
        const rawName = li.name || li.menuId;
        if (!rawName) continue;
        const norm = normalizeMenuName(rawName);
        if (!norm) continue;
        const matchedToCurrent = out.some(m => isMenuMatch(m.name, rawName));
        if (matchedToCurrent) continue;
        if (seenNorm.has(norm)) continue;
        const q = Number(li.qty) || Number(li.quantity) || 0;
        const rev = Number(li.total) || Number(li.amount) || (q * (Number(li.unitPrice) || 0));
        if (!legacyAgg[norm]) { legacyAgg[norm] = { name: rawName, qty: 0, revenue: 0 }; }
        legacyAgg[norm].qty += q;
        legacyAgg[norm].revenue += rev;
      }
    });

    const legacyList = Object.entries(legacyAgg)
      .map(([norm, info]) => ({
        id: `legacy_${norm}`,
        name: info.name,
        price: info.qty > 0 ? Math.round(info.revenue / info.qty) : 0,
        category: '판매 중단',
        isLegacy: true,
        legacyQty: info.qty,
      }))
      .filter(m => m.price > 0)
      .sort((a, b) => b.legacyQty - a.legacyQty);

    legacyList.forEach(m => {
      seenNorm.add(normalizeMenuName(m.name));
      out.push(m);
    });

    return out;
  }, [categories, transactions]);

  // 모달 reset + 기존 데이터 preload
  useEffect(() => {
    if (isOpen) {
      const d = defaultDate || today;
      setDate(d);
      setAmounts({ sales_card: 0, sales_cash: 0, sales_delivery: 0 });
      setMemo('');
      setError('');

      // 해당 날짜의 기존 lineItems 모아서 menuQtys preload
      const dayLineItems = [];
      (transactions || []).forEach(t => {
        if (t?.type !== 'income') return;
        if (t.date !== d) return;
        if (!Array.isArray(t.lineItems)) return;
        for (const li of t.lineItems) {
          dayLineItems.push(li);
        }
      });

      if (dayLineItems.length > 0) {
        const preloaded = {};
        for (const li of dayLineItems) {
          const rawName = li.name || li.menuId;
          if (!rawName) continue;
          const qty = Number(li.qty) || Number(li.quantity) || 0;
          if (qty <= 0) continue;
          const matched = availableMenus.find(m => isMenuMatch(m.name, rawName));
          if (matched) {
            preloaded[matched.id] = (preloaded[matched.id] || 0) + qty;
          }
        }
        setMenuQtys(preloaded);
        setMode(Object.keys(preloaded).length > 0 ? 'menus' : 'total');
      } else {
        setMenuQtys({});
        setMode('total');
      }
    }
  }, [isOpen, defaultDate, transactions, availableMenus]);

  // ⚠️ 모든 hook은 early return 위에 있어야 함 (React hook order 규칙)
  const menuLineItems = useMemo(() => {
    return availableMenus
      .filter(m => (Number(menuQtys[m.id]) || 0) > 0)
      .map(m => ({
        menuId: m.id, name: m.name,
        qty: Number(menuQtys[m.id]) || 0,
        unitPrice: m.price,
        total: (Number(menuQtys[m.id]) || 0) * m.price,
      }));
  }, [availableMenus, menuQtys]);

  if (!isOpen) return null;

  // 합계 계산
  const totalFromAmounts = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);
  const totalFromMenus = menuLineItems.reduce((s, l) => s + l.total, 0);
  const total = mode === 'total' ? totalFromAmounts : totalFromMenus;

  const handleSave = async () => {
    if (total <= 0) {
      setError(L("매출 금액을 입력해주세요"));
      return;
    }
    // 기존 거래 덮어쓰기 안전 확인 — 5건 이상 또는 합계 차이 30% 이상이면 confirm
    if (existingDayIncomes.length > 0) {
      const existingTotal = existingDayIncomes.reduce((s, t) => s + ((Number(t.supply) || 0) + (Number(t.vat) || 0)), 0);
      const diff = Math.abs(total - existingTotal);
      const diffPct = existingTotal > 0 ? (diff / existingTotal * 100) : 0;
      if (existingDayIncomes.length >= 5 || diffPct >= 30) {
        const ok = window.confirm(
          L("⚠️ {0} 기존 매출 {1}건 (합계 {2})을 삭제하고\n", date, existingDayIncomes.length, M(existingTotal)) +
          L("새 매출 {0}으로 덮어씁니다.\n\n", M(total)) +
          L("(차이: {0}%)\n\n", Math.round(diffPct)) +
          L("계속할까요?")
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const txs = [];
      if (mode === 'total') {
        // 모드 A: 카테고리별로 transaction 생성
        for (const t of REVENUE_TYPES) {
          const amt = Number(amounts[t.id]) || 0;
          if (amt <= 0) continue;
          const supply = Math.round(amt / 1.1);
          const vat = amt - supply;
          txs.push({
            type: 'income',
            date,
            supply, vat, total: amt,
            partnerId: null, partnerName: '',
            evidence: { type: t.id === 'sales_cash' ? 'cash' : 'card', isQualified: true, sourceUrl: null, externalId: null },
            category: { major: null, sub: null },
            revenueCategory: t.id,
            itemName: '',
            memo: memo || `${t.label} 매출 (빠른 입력)`,
            vatTreatment: { deductible: false, presumed: false, presumedRate: null },
          });
        }
      } else {
        // 모드 B: 결제수단 분할 기준으로 transaction 1개씩 생성 + lineItems에 메뉴 데이터 첨부
        const splitTotal = (paymentSplit.sales_card + paymentSplit.sales_cash + paymentSplit.sales_delivery) || 100;
        for (const t of REVENUE_TYPES) {
          const ratio = (paymentSplit[t.id] || 0) / splitTotal;
          if (ratio <= 0) continue;
          const amt = Math.round(total * ratio);
          if (amt <= 0) continue;
          const supply = Math.round(amt / 1.1);
          const vat = amt - supply;
          txs.push({
            type: 'income',
            date,
            supply, vat, total: amt,
            partnerId: null, partnerName: '',
            evidence: { type: t.id === 'sales_cash' ? 'cash' : 'card', isQualified: true, sourceUrl: null, externalId: null },
            category: { major: null, sub: null },
            revenueCategory: t.id,
            itemName: '',
            // 메뉴 판매 데이터 — 첫 transaction에만 lineItems 첨부 (중복 방지)
            ...(t.id === REVENUE_TYPES[0].id ? { lineItems: menuLineItems } : {}),
            memo: memo || `${t.label} 매출 (메뉴별 입력 ${menuLineItems.length}종)`,
            vatTreatment: { deductible: false, presumed: false, presumedRate: null },
          });
        }
      }
      // 같은 날짜 기존 매출 거래 ID들 → 부모가 삭제하도록 전달 (덮어쓰기 패턴)
      const replaceIds = existingDayIncomes.map(t => t.id).filter(Boolean);
      await onSave(txs, { replaceIds, replaceDate: date });
      onClose();
    } catch (e) {
      setError(e.message || L("저장 실패"));
    } finally {
      setSaving(false);
    }
  };

  // ── 모달 ──
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff',
        borderRadius: 16,
        padding: isMobile ? '20px 18px' : '24px 26px',
        maxWidth: 520, width: '100%',
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 12px 60px rgba(0,0,0,0.3)',
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a' }}>
              {L("✏️ 빠른 매출 입력")}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              {L("거래장부에 자동 저장 · 메뉴별 입력 시 이익률 정확도 ↑")}
            </div>
          </div>
          <button onClick={onClose} aria-label={L("닫기")} style={{
            width: 30, height: 30, borderRadius: '50%',
            border: '1px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 16, cursor: 'pointer',
          }}>×</button>
        </div>

        {/* 모드 토글 */}
        <div style={{
          display: 'flex', gap: 4,
          background: '#f3f4f6', borderRadius: 9, padding: 4,
          marginBottom: 14,
        }}>
          <ModeBtn active={mode === 'total'} onClick={() => setMode('total')}>
            {L("💰 총액 입력")}
          </ModeBtn>
          <ModeBtn active={mode === 'menus'} onClick={() => setMode('menus')}
            disabled={availableMenus.length === 0}
            tip={availableMenus.length === 0 ? L("판매가 등록된 메뉴 없음") : null}>
            {L("🍽️ 메뉴별 입력")} {availableMenus.length > 0 && L("({0}종)", availableMenus.length)}
          </ModeBtn>
        </div>

        {/* 날짜 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📅 매출 날짜")}
          </label>
          <input type="date" value={date} max={today}
            onChange={e => setDate(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 14, outline: 'none',
            }}
          />
        </div>

        {/* ── 모드 A: 총액 입력 ── */}
        {mode === 'total' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
              {L("💰 결제 수단별 매출 (입력 안 한 항목은 0원)")}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {REVENUE_TYPES.map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  background: amounts[t.id] > 0 ? `${t.color}10` : '#fafbfc',
                  borderRadius: 8,
                  border: `1.5px solid ${amounts[t.id] > 0 ? t.color : '#e5e7eb'}`,
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{t.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.color, width: 40, flexShrink: 0 }}>
                    {t.label}
                  </span>
                  <input type="number" min="0"
                    value={amounts[t.id] || ''}
                    placeholder={t.placeholder}
                    onChange={e => setAmounts({ ...amounts, [t.id]: Number(e.target.value) || 0 })}
                    style={{
                      flex: 1, minWidth: 0,
                      padding: '7px 10px',
                      border: '1px solid #e5e7eb', borderRadius: 6,
                      fontSize: 14, textAlign: 'right',
                      outline: 'none', background: '#fff',
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{MU()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 모드 B: 메뉴별 입력 ── */}
        {mode === 'menus' && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
                {L("🍽️ 메뉴별 판매 수량")}
              </label>
              <div style={{
                maxHeight: 280, overflowY: 'auto',
                border: '1px solid #e5e7eb', borderRadius: 8,
                background: '#fafbfc',
              }}>
                {availableMenus.map((m, idx) => {
                  const qty = Number(menuQtys[m.id]) || 0;
                  const prevLegacy = idx > 0 && availableMenus[idx - 1].isLegacy;
                  const showDivider = m.isLegacy && !prevLegacy;
                  return (
                    <Fragment key={m.id}>
                      {showDivider && (
                        <div style={{
                          padding: '6px 10px', fontSize: 10, color: '#52525B',
                          background: '#FAFAFA', borderBottom: '1px solid #f0f0f0',
                          fontWeight: 700,
                        }}>
                          {L("⚠️ 판매 중단 메뉴 (과거 거래장부에서 발견)")}
                        </div>
                      )}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px',
                      borderBottom: '1px solid #f0f0f0',
                      background: qty > 0 ? '#FAFAFA' : (m.isLegacy ? '#fafaf7' : 'transparent'),
                      opacity: m.isLegacy && qty === 0 ? 0.7 : 1,
                    }}>
                      <span style={{
                        flex: 1, minWidth: 0,
                        fontSize: 13, fontWeight: qty > 0 ? 700 : 500,
                        color: m.isLegacy ? '#6b7280' : '#1a1a1a',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {m.isLegacy && <span style={{ marginRight: 4, fontSize: 10 }}>💤</span>}
                        {m.name}
                      </span>
                      <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0, width: 56, textAlign: 'right' }}>
                        {M(m.price)}
                      </span>
                      <button onClick={() => setMenuQtys({ ...menuQtys, [m.id]: Math.max(0, qty - 1) })}
                        style={{ width: 24, height: 24, border: '1px solid #e5e7eb',
                                 background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 14 }}>−</button>
                      <input type="number" min="0"
                        value={qty || ''}
                        placeholder="0"
                        onChange={e => setMenuQtys({ ...menuQtys, [m.id]: Math.max(0, Number(e.target.value) || 0) })}
                        style={{
                          width: 50, padding: '4px 6px',
                          border: '1px solid #e5e7eb', borderRadius: 5,
                          fontSize: 13, textAlign: 'center',
                          outline: 'none', background: '#fff',
                        }}
                      />
                      <button onClick={() => setMenuQtys({ ...menuQtys, [m.id]: qty + 1 })}
                        style={{ width: 24, height: 24, border: '1px solid #FF6F0F',
                                 background: '#FAFAFA', color: '#18181B',
                                 borderRadius: 5, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>+</button>
                      <span style={{
                        fontSize: 11, color: qty > 0 ? '#FF6F0F' : '#9ca3af',
                        fontWeight: 700, width: 60, textAlign: 'right', flexShrink: 0,
                      }}>{M(qty * m.price)}</span>
                    </div>
                    </Fragment>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                {L("💡 메뉴별 입력 시 메뉴 이익률 랭킹이 실제 판매 기반으로 정확해져요")}
              </div>
            </div>

            {/* 결제수단 분할 % */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
                {L("💳 결제수단 비율 (%) — 합계 자동으로 분배")}
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {REVENUE_TYPES.map(t => (
                  <div key={t.id} style={{
                    flex: 1, padding: '6px 8px',
                    background: `${t.color}10`,
                    border: `1px solid ${t.color}40`,
                    borderRadius: 7,
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <span style={{ fontSize: 12 }}>{t.icon}</span>
                    <span style={{ fontSize: 11, color: t.color, fontWeight: 700 }}>{t.label}</span>
                    <input type="number" min="0" max="100"
                      value={paymentSplit[t.id]}
                      onChange={e => setPaymentSplit({ ...paymentSplit, [t.id]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                      style={{
                        flex: 1, padding: '3px 5px',
                        border: 'none', background: '#fff',
                        borderRadius: 4, fontSize: 12,
                        textAlign: 'right', outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: 10, color: '#6b7280' }}>%</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* 합계 */}
        <div style={{
          padding: '10px 14px',
          background: total > 0 ? '#FAFAFA' : '#f3f4f6',
          borderRadius: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: total > 0 ? '#FF6F0F' : '#9ca3af' }}>
            {L("이날 매출 합계")}
            {mode === 'menus' && menuLineItems.length > 0 &&
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
                {' '}{L("· {0}종", menuLineItems.length)} {menuLineItems.reduce((s, l) => s + l.qty, 0)}{L("개 판매")}
              </span>}
          </span>
          <span style={{ fontSize: 18, fontWeight: 900, color: total > 0 ? '#FF6F0F' : '#9ca3af' }}>
            {M(total)}
          </span>
        </div>

        {/* 메모 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📝 메모 (선택)")}
          </label>
          <input type="text"
            value={memo} onChange={e => setMemo(e.target.value)}
            placeholder={L("예: 비 와서 매출 적음 / 단체 회식 30명")}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 13, outline: 'none',
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', background: '#F4F4F5',
            color: '#52525B', borderRadius: 8,
            fontSize: 12, marginBottom: 10,
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* 안내 */}
        <div style={{
          padding: '8px 12px', background: '#FAFAFA',
          borderRadius: 8, fontSize: 11, color: '#78350f',
          lineHeight: 1.5, marginBottom: 12,
        }}>
          {L("💡 부가세 포함 금액으로 저장 (공급가액 = 입력값 ÷ 1.1, 부가세 = 10%). 거래장부에서 수정 가능.")}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '11px',
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 13, fontWeight: 700,
            borderRadius: 9, cursor: 'pointer',
          }}>{L("취소")}</button>
          <button onClick={handleSave} disabled={saving || total <= 0} style={{
            flex: 2, padding: '11px',
            border: 'none',
            background: total > 0 && !saving ? '#EA580C' : '#9ca3af',
            color: '#fff', fontSize: 13, fontWeight: 800,
            borderRadius: 9, cursor: total > 0 && !saving ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {saving ? L("⏳ 저장 중...") : L("✅ {0} 저장", M(total))}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 모드 토글 버튼 ──
function ModeBtn({ active, onClick, disabled, tip, children }) {
  return (
    <button onClick={onClick} disabled={disabled} title={tip || ''} style={{
      flex: 1, padding: '8px 10px',
      border: 'none',
      background: active ? '#FF6F0F' : 'transparent',
      color: active ? '#fff' : (disabled ? '#9ca3af' : '#6b7280'),
      fontSize: 12, fontWeight: 800,
      borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s',
      opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}
